import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { type Option } from '@/components/common/Form/types'
import { useActiveManagementTimezonesQuery } from '@/features/ActiveManagement/hooks/useActiveManagementTimezonesQuery'
import { SettingsTextCard } from '@/components/molecules/SettingsTextCard/SettingsTextCard'
import { DEFAULT_INTERVAL_REPORT_TIMEZONE_ENTRY } from '@/utils/constants'
import { RadioOptionPicker } from '@/components/common/Form/RadioOptionPicker/RadioOptionPicker'
import { WidgetSpinner } from '@/components'
import { Icon } from '@/components/atoms'
import { formatUtcOffset } from '@/features/ActiveManagement/utils/formatUtcOffset'

export const TimezoneSelect: React.FC<{
    value: string
    onChange: (timezone: string) => void
}> = (props) => {
    const { value, onChange } = props
    const { data, isLoading, isError, isFetched, isFetching } =
        useActiveManagementTimezonesQuery()
    const { t } = useTranslation('translations')

    const onChangeRef = useRef<typeof onChange>(onChange)

    useEffect(() => {
        onChangeRef.current = onChange
    }, [onChange])

    const options: Array<Option<string>> = (data ?? []).map((item) => ({
        value: item.timezone,
        label: item.timezone,
        subtitle: formatUtcOffset(item.utcOffset),
    }))

    const defaultOption = useMemo(() => {
        if (value !== '') {
            const option = options.find((option) => option.value === value)

            if (option) {
                return option
            }
        }

        const defaultTimezoneEntry = DEFAULT_INTERVAL_REPORT_TIMEZONE_ENTRY
        const defaultOption: Option<string> = {
            value: defaultTimezoneEntry.timezone,
            label: defaultTimezoneEntry.timezone,
            subtitle: formatUtcOffset(defaultTimezoneEntry.utcOffset),
        }

        if (!options.length) return defaultOption

        const option = options.find(
            (option) => option.value === defaultTimezoneEntry.timezone
        )
        if (option) return option

        return options[0]
    }, [value, options])

    const isLoadingValue = isLoading || isFetching || !isFetched

    useEffect(() => {
        const onChange = onChangeRef.current

        if (isLoadingValue) {
            // Reset value when timezones are still loading
            onChange('')
            return
        }

        onChange(defaultOption.value)
    }, [options.length, defaultOption.value, isLoadingValue])

    const renderLabel = useCallback((option: Option) => {
        return (
            <div className="flex flex-col">
                <div className="text-label-small text-regular">
                    {option.label}
                </div>
                {option.subtitle && (
                    <div className="text-body-small font-normal text-subtle">
                        {option.subtitle}
                    </div>
                )}
            </div>
        )
    }, [])

    const noValueLabel = useMemo(() => {
        if (isLoadingValue) {
            return <WidgetSpinner />
        }

        if (isError) {
            return <Icon name="error" className="text-red-500" size="small" />
        }

        return null
    }, [isError, isLoadingValue])

    if (!isLoadingValue && (!data?.length || data.length === 1)) {
        const title = defaultOption.label
        const subtitle = defaultOption.subtitle

        return (
            <SettingsTextCard
                title={title}
                subtitle={subtitle}
                description={t(
                    'active_management.interval.report_settings.timezone.default_description'
                )}
            />
        )
    }

    return (
        <RadioOptionPicker
            name="timezone"
            onChange={(value: unknown) => {
                if (typeof value === 'string') {
                    onChange(value)
                }
            }}
            value={value}
            noValueLabel={noValueLabel}
            defaultValue={defaultOption.value}
            enableDefaultSelection
            options={options}
            renderLabel={renderLabel}
            label={t(
                'active_management.interval.report_settings.timezone.label'
            )}
            description={t(
                'active_management.interval.report_settings.timezone.description'
            )}
            className="max-w-60"
        />
    )
}
